import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { API, api } from "../../lib/api";
import { layoutTiles, normalizeSettings } from "./watermarkLayout";

/**
 * Phase 224 - the municipality's logo watermark behind an on-screen document (the receipt sheet).
 *
 * Reads the public settings once per municipality (`/api/public/{code}/watermark`) and draws <img> tiles with the
 * same geometry as the PDF (see watermarkLayout.js). Plain <img> elements - not CSS backgrounds - so the browser
 * prints them without any "print background graphics" setting. Renders nothing when the municipality has no logo or
 * switched the watermark off. The parent must be `position: relative`; put this component FIRST inside it so the
 * document content paints on top.
 *
 * `settings` / `logoUrl` can be passed explicitly (the Super Admin live preview); otherwise they are fetched.
 */
const cache = new Map();   // code -> promise of public settings

export function fetchWatermarkSettings(code, force = false) {
  if (!code) return Promise.resolve(null);
  if (force || !cache.has(code)) {
    cache.set(code, api.get(`/public/${code}/watermark`).then((r) => r.data).catch(() => null));
  }
  return cache.get(code);
}

export function watermarkLogoUrl(code, version = "") {
  return `${API}/public/${code}/watermark/logo${version ? `?v=${encodeURIComponent(version)}` : ""}`;
}

export function WatermarkOverlay({ code, settings: givenSettings, logoUrl: givenLogo, logoSize, className = "", testId = "watermark-overlay" }) {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [fetched, setFetched] = useState(null);
  const [imgSize, setImgSize] = useState(null);

  useEffect(() => {
    if (givenSettings) return undefined;
    let alive = true;
    fetchWatermarkSettings(code).then((s) => { if (alive) setFetched(s || { active: false }); });
    return () => { alive = false; };
  }, [code, givenSettings]);

  const s = givenSettings ? { ...normalizeSettings(givenSettings), active: givenSettings.enabled !== false && !!(givenLogo || givenSettings.has_logo) } : fetched;
  const logoUrl = givenLogo || (s?.has_logo ? watermarkLogoUrl(code, s.logo_version) : null);
  const active = !!(s && s.active && logoUrl);

  // measure the parent (the document sheet) and follow its size
  useLayoutEffect(() => {
    const el = ref.current?.parentElement;
    if (!el || !active) return undefined;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);

  if (!active) return null;
  const lw = logoSize?.w || imgSize?.w || s.logo_w || 1;
  const lh = logoSize?.h || imgSize?.h || s.logo_h || 1;
  const tiles = layoutTiles(box.w, box.h, lw, lh, s);
  return (
    <div ref={ref} aria-hidden="true" data-testid={testId} data-position={s.position} data-repeat={s.repeat}
      className={`absolute inset-0 overflow-hidden pointer-events-none select-none ${className}`}
      style={{ zIndex: 0, printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
      {tiles.map((t, i) => (
        <img key={i} src={logoUrl} alt="" draggable={false}
          onLoad={!imgSize && !logoSize ? (e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight }) : undefined}
          className="absolute"
          style={{ left: t.x, top: t.y, width: t.w, height: t.h, opacity: s.opacity_pct / 100,
                   filter: s.color_mode === "grey" ? "grayscale(1)" : "none" }} />
      ))}
    </div>
  );
}
